import { LoadingButton } from "@mui/lab";
import { Box, Button, Dialog, DialogActions, DialogContent } from "@mui/material";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { addLensesToFirm, deleteLensesFromFirm, selectLenses } from "redux/slices/client";
import { P } from "theme/typography";
import { FirmWithLenses } from "generated/graphql";
import PCheckbox from "components/PCheckbox";
import RequestStatus from "redux/slices/RequestStatus";
import { useSnackbar } from "notistack";

interface FirmLensPickerProps {
  open: boolean;
  onClose: () => void;
  firm: FirmWithLenses;
}

const FirmLensPicker = (props: FirmLensPickerProps) => {
  const [status, setStatus] = useState<RequestStatus>(RequestStatus.IDLE);
  const lenses = useAppSelector(selectLenses);
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const [addLenses, setAddLenses] = useState<number[]>([]);
  const [removeLenses, setRemoveLenses] = useState<number[]>([]);

  // reset add/remove lenses when firm changes
  useEffect(() => {
    setAddLenses([]);
    setRemoveLenses([]);
  }, [props.firm]);

  const handleAddLenses = () => {
    if (addLenses.length > 0) {
      return dispatch(
        addLensesToFirm({
          firmId: props.firm.id,
          lensIds: addLenses,
        })
      );
    } else {
      return Promise.resolve();
    }
  };

  const handleRemoveLenses = () => {
    if (removeLenses.length > 0) {
      return dispatch(
        deleteLensesFromFirm({
          firmId: props.firm.id,
          lensIds: removeLenses,
        })
      );
    } else {
      return Promise.resolve();
    }
  };

  const handleSave = () => {
    if (!props.firm) return;
    setStatus(RequestStatus.PENDING);
    Promise.all([handleAddLenses(), handleRemoveLenses()]).then(() => {
      props.onClose();
      setStatus(RequestStatus.SUCCEEDED);
      enqueueSnackbar(`Firm lenses updated successfully`, {
        variant: "success",
      });
    });
  };
  return (
    <Dialog
      open={props.open}
      onClose={() => {
        props.onClose();
      }}>
      <DialogContent>
        <P variant="h5">Firm Lenses</P>
        {props.firm ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "400px",
              mt: 2,
            }}>
            <P
              sx={{
                mb: 1,
              }}>
              Select lenses to attach to this firm
            </P>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                maxHeight: "300px",
                overflow: "auto",
              }}>
              {lenses.map((lens) => (
                <Box
                  key={lens.id}
                  sx={{
                    ml: 2,
                    width: "100%",
                  }}>
                  <PCheckbox
                    checked={
                      addLenses.includes(lens.id) ||
                      (!removeLenses.includes(lens.id) && props.firm.lensIds.includes(lens.id))
                    }
                    label={lens.name}
                    onChange={(e) => {
                      if (props.firm === undefined) return;
                      else {
                        if (e.target.checked) {
                          // If the lens is not already attached to the firm, add it to the addLenses array
                          if (!props.firm.lensIds.includes(lens.id)) {
                            setAddLenses([...addLenses, lens.id]);
                          }
                          // If the lens is already attached to the firm, remove it from the removeLenses array
                          else {
                            setRemoveLenses(removeLenses.filter((lensId) => lensId !== lens.id));
                          }
                        } else {
                          // If the lens is already attached to the firm, add it to the removeLenses array
                          if (props.firm.lensIds.includes(lens.id)) {
                            setRemoveLenses([...removeLenses, lens.id]);
                          }
                          // If the lens is not attached to the firm, remove it from the addLenses array
                          else {
                            setAddLenses(addLenses.filter((lensId) => lensId !== lens.id));
                          }
                        }
                      }
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        ) : (
          <P>There was an error loading the firm</P>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            props.onClose();
          }}>
          Cancel
        </Button>
        <LoadingButton
          loading={status === RequestStatus.PENDING}
          onClick={() => {
            handleSave();
          }}
          variant="contained">
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default FirmLensPicker;
